import {
  encodeCursor, encodeViewport,
  encodeStrokeBegin, encodeStrokeAppend, encodeStrokeCommit, encodeStrokeCommitFull, encodeStrokeAbort, encodeErase, encodeUnerase,
  decodeFrame, decodeJson,
  WS_FRAME_JSON, CURSOR_THROTTLE_MS, VIEWPORT_THROTTLE_MS,
} from '@blackboard/shared';
import type { Point, Tool } from '@blackboard/shared';

export type WsStatus =
  | { t: 'connecting' }
  | { t: 'open' }
  | { t: 'reconnecting'; nextDelayMs: number; reason?: string }
  | { t: 'fatal'; code: number; reason: string };

export type WsEvent =
  | { t: 'hello'; server_time: string; board: any; online: any[]; last_stroke_id: number }
  | { t: 'presence:join'; user: any }
  | { t: 'presence:leave'; user_id: string }
  | { t: 'cursor'; user_id: string; x: number; y: number }
  | { t: 'stroke:begin'; user_id: string; remote_id: string; tool: Tool; color: string; size: number; p0: [number, number, number] }
  | { t: 'stroke:append'; remote_id: string; points: [number, number, number][] }
  | { t: 'stroke:commit'; remote_id: string; stroke_id: number; bbox: [number, number, number, number] }
  | { t: 'strokes:add'; strokes: Array<{ remote_id: string; stroke_id: number; user_id: string; tool: Tool; color: string; size: number; bbox: [number, number, number, number]; points: [number, number, number][] }> }
  | { t: 'stroke:abort'; remote_id: string }
  | { t: 'stroke:erase'; stroke_ids: number[]; by_user_id: string; ev_id?: number }
  | { t: 'strokes:restore'; strokes: Array<{ stroke_id: number; user_id: string; tool: Tool; color: string; size: number; bbox: [number, number, number, number]; points: [number, number, number][] }> }
  | { t: 'board:cleared'; by_admin_id: number; scope: string }
  | { t: 'error'; code: string; reason: string };

export class WsClient {
  private ws: WebSocket | null = null;
  /**
   * Socket generation. Every openSocket() bumps it and captures the value in
   * that socket's handlers; close()/detach strips handlers AND bumps again.
   * A socket that survives its own replacement (StrictMode double-mount,
   * HMR, close-during-CONNECTING races) can therefore never deliver events
   * into the new generation's state — the exact bug where a zombie socket
   * echoed our own stroke:begin back and clobbered the in-flight stroke.
   */
  private gen = 0;
  private backoff = 500;
  private onEventCb: ((e: WsEvent) => void) | null = null;
  private onReopenCb: (() => void) | null = null;
  private onStatusCb: ((s: WsStatus) => void) | null = null;
  private lastCursorSent = 0;
  private lastViewportSent = 0;
  private closed = false;
  private hadOpen = false;

  constructor(private wsUrl: string, private token: string, private boardSlug: string) {}

  connect(onEvent: (e: WsEvent) => void, onReopen?: () => void, onStatus?: (s: WsStatus) => void) {
    this.onEventCb = onEvent;
    this.onReopenCb = onReopen ?? null;
    this.onStatusCb = onStatus ?? null;
    // React 18 StrictMode dev mounts effects twice (mount → cleanup → mount).
    // The cleanup called close() which latched `closed`; un-latch here so the
    // second mount reconnects. Generations keep any leftover socket inert.
    this.closed = false;
    this.openSocket();
  }

  /** Detach handlers + close the current socket, if any. Always safe to call. */
  private detachSocket() {
    const ws = this.ws;
    this.ws = null;
    this.gen += 1; // invalidate any handler still closing asynchronously
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try { ws.close(); } catch { /* already closed */ }
  }

  private openSocket() {
    if (this.closed) return;
    this.detachSocket();
    const gen = this.gen;
    this.onStatusCb?.(
      this.hadOpen
        ? { t: 'reconnecting', nextDelayMs: this.backoff }
        : { t: 'connecting' }
    );
    const url = `${this.wsUrl}/ws?token=${encodeURIComponent(this.token)}&board=${encodeURIComponent(this.boardSlug)}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      if (gen !== this.gen) return;
      this.backoff = 500;
      if (this.hadOpen) this.onReopenCb?.();
      this.hadOpen = true;
      this.onStatusCb?.({ t: 'open' });
    };
    ws.onmessage = (ev) => {
      if (gen !== this.gen) return;
      const buf = ev.data as ArrayBuffer;
      const { type, rest } = decodeFrame(new Uint8Array(buf));
      if (type === WS_FRAME_JSON) {
        try {
          this.onEventCb?.(decodeJson<WsEvent>(rest));
        } catch {}
      }
    };
    ws.onclose = (ev) => {
      if (gen !== this.gen) return;
      if (this.ws === ws) this.ws = null;
      if (this.closed) return;
      // 1008 policy violation = auth/board failure: stop reconnecting and let app react.
      // 1013 try again later = rate limit: also fatal so client doesn't hammer the server,
      // but ConnectionBanner shows a different message and we don't logout.
      if (ev.code === 1008 || ev.code === 1013) {
        this.closed = true;
        this.onStatusCb?.({ t: 'fatal', code: ev.code, reason: ev.reason || 'policy_violation' });
        return;
      }
      const delay = this.backoff;
      this.onStatusCb?.({ t: 'reconnecting', nextDelayMs: delay, reason: ev.reason });
      setTimeout(() => this.openSocket(), delay);
      this.backoff = Math.min(this.backoff * 2, 8000);
    };
    ws.onerror = () => {
      if (gen !== this.gen) return;
      try { ws.close(); } catch {}
    };
  }

  private send(buf: Uint8Array) {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(buf);
  }

  sendCursor(x: number, y: number) {
    const now = Date.now();
    if (now - this.lastCursorSent < CURSOR_THROTTLE_MS) return;
    this.lastCursorSent = now;
    this.send(encodeCursor(x, y));
  }

  sendViewport(x1: number, y1: number, x2: number, y2: number, zoom: number) {
    const now = Date.now();
    if (now - this.lastViewportSent < VIEWPORT_THROTTLE_MS) return;
    this.lastViewportSent = now;
    this.send(encodeViewport(x1, y1, x2, y2, zoom));
  }

  strokeBegin(local_id: number, tool: Tool, size: number, color: string, p: Point) {
    this.send(encodeStrokeBegin(local_id, tool, size, color, p.x, p.y, p.pressure));
  }
  strokeAppend(local_id: number, pts: Point[]) {
    if (pts.length === 0) return;
    this.send(encodeStrokeAppend(local_id, pts));
  }
  strokeCommit(local_id: number) {
    this.send(encodeStrokeCommit(local_id));
  }
  /** v2: whole stroke in one frame — no begin/append stream. */
  strokeCommitFull(local_id: number, tool: Tool, size: number, color: string, pts: Point[]) {
    if (pts.length === 0) return;
    this.send(encodeStrokeCommitFull(local_id, tool, size, color, pts));
  }
  strokeAbort(local_id: number) {
    this.send(encodeStrokeAbort(local_id));
  }
  erase(stroke_ids: number[]) {
    if (stroke_ids.length > 0) this.send(encodeErase(stroke_ids));
  }
  /** 撤销一次擦除（恢复该审计事件对应的笔画）。 */
  unerase(event_id: number) {
    this.send(encodeUnerase(event_id));
  }

  close() {
    this.closed = true;
    this.detachSocket();
  }
}
